import { MapHelper } from 'Plugins/FloorPlan/Helpers/MapHelper';
import { getMapByType } from 'Plugins/FloorPlan/Utils/Room/MapByType';
import React from 'react';

import { ProduitDto } from '../../../../../Models/Questions/ProduitDto';
import { useViewState } from '../../../../../Web/Hooks/ViewState/ViewState';
import { MapContextState, useMapContextState } from '../../../Context/MapContext';
import { IOpening } from '../../../Models/IOpening';
import { IRoom, RoomType } from '../../../Models/IRoom';
import { IRoomItem } from '../../../Models/IRoomItem';
import {
    LaizeCalculatorResult,
    LaizeProps,
    LaizeStairsCalculatorResult,
    LameFlooringLayout,
    ProductType,
    StairsResult,
} from '../Laize';
import { CalculLaizeParams, LaizeCalc, LaizeCalcProps } from '../LaizeCalc';
import { LaizeBandItemV3 } from '../v3/LaizeBandTrace';
import { LaizeV3 } from '../v3/LaizeV3';
import { LaizeStairs } from '../Stairs/LaizeStairs';

type MapInputs = Partial<{ rooms: IRoom[]; laizeProps: LaizeProps[] }>;

export type FocusedBands = {
    bandMosaicFocused?: number | string;
    bandsFocused?: LaizeBandItemV3[];
    stairsBandFocused?: StairsResult[];
    focusedFrom?: 'FromLaizeBandMap' | 'FromLaizeBandMosaic';
};

export type ZoomedBands = { roomId?: string; bandId?: number | string; index?: number };

export type ZoomedStair = { bandId?: string; stairId?: string, bandIndex?: number };

type LaizeCalcDialogValue = {
    laizeResult?: LaizeCalculatorResult;
    laizeStairsResult?: LaizeStairsCalculatorResult;
    validCurrentWallStarts: Array<{
        roomId: string;
        isValid: boolean;
    }>;

    state: LaizeCalcDialogState;
    update: (values: LaizeCalcDialogState) => void;

    focusedBands?: FocusedBands;
    setFocusedBands: (bands?: FocusedBands) => void;
    clearFocusedBands: () => void;

    zoomedBands?: ZoomedBands;
    setZoomedBands: (bands?: ZoomedBands) => void;

    zoomedStair?: ZoomedStair;
    setZoomedStair: (stair?: ZoomedStair) => void;
};
const LaizeCalcDialogContext = React.createContext<LaizeCalcDialogValue>({
    state: toLaizeCalcDialogState({}),
    update: () => {},
    setFocusedBands: () => {},
    clearFocusedBands: () => {},
    setZoomedBands: () => {},
    setZoomedStair: () => {},
    validCurrentWallStarts: [],
});
export const useLaizeCalcDialog = () => React.useContext(LaizeCalcDialogContext);
export const useLaizeCalcDialogState = () => React.useContext(LaizeCalcDialogContext).state;
export const useLaizeCalcDialogUpdate = () => React.useContext(LaizeCalcDialogContext).update;

type LaizeCalcDialogState = Partial<{
    mapInputs: MapInputs;

    showParams: boolean;

    roomType?: RoomType;
    rooms: IRoom[];
    openings: IOpening[];
    roomItems: IRoomItem[];

    produit: ProduitDto;
    productType?: ProductType;

    //* EDITOR PARAMS
    laizeProps: LaizeProps;
    iteration: number;
    rotationAngle: number;
    bandWidth: number;
    longueurCm: number;
    raccordLength: number;
    laizeMargin: number;
    motifLength: number;
    motifWidth: number;
    laizeConsecutiveBandsMargin: number;
}>;

export const LaizeCalcDialogProvider = ({ children }: React.PropsWithChildren<{}>) => {
    const originalMap = useMapContextState();

    const [focusedBands, setFocusedBands] = React.useState<FocusedBands>();
    const [zoomedBands, setZoomedBands] = React.useState<ZoomedBands>();
    const [zoomedStair, setZoomedStair] = React.useState<ZoomedStair>();
    const [laizeResult, setLaizeResult] = React.useState<{
        result?: LaizeCalculatorResult;
        props?: LaizeCalcProps;
        validCurrentWallStarts: Array<{ roomId: string; isValid: boolean }>;
    }>({ validCurrentWallStarts: [] });
    const [laizeStairsResult, setLaizeStairsResult] = React.useState<LaizeStairsCalculatorResult>();
    const { state, getState, update } = useViewState<LaizeCalcDialogState>(toLaizeCalcDialogState(originalMap));

    const clearFocusedBands = () => setFocusedBands(undefined);

    React.useEffect(() => {
        const {
            mapInputs = {},
            laizeProps = {},
            rooms = [],
            openings = [],
            roomItems = [],
            roomType = RoomType.Sol,
        } = getState();

        const laizeCalcProps = LaizeCalc.toLaizeCalcProps(laizeProps);
        laizeCalcProps.BAND_WIDTH = state.bandWidth || laizeCalcProps.BAND_WIDTH;
        laizeCalcProps.LAIZE_MARGIN = state.laizeMargin || laizeCalcProps.LAIZE_MARGIN;
        laizeCalcProps.RACCORD_SIZE = state.raccordLength || laizeCalcProps.RACCORD_SIZE;
        laizeCalcProps.MARGIN_WIDTH = state.motifWidth || laizeCalcProps.MARGIN_WIDTH;
        laizeCalcProps.MARGIN_LENGTH = state.motifLength || laizeCalcProps.MARGIN_LENGTH;
        laizeCalcProps.LAIZE_CONSECUTIVE_BANDS_MARGIN =
            state.laizeConsecutiveBandsMargin || laizeCalcProps.LAIZE_CONSECUTIVE_BANDS_MARGIN;
        laizeCalcProps.LONGUEUR_CM = state.longueurCm || laizeCalcProps.LONGUEUR_CM;
        laizeCalcProps.TYPE = state.productType || laizeCalcProps.TYPE;

        const originalMapWalls = MapHelper.getWalls(originalMap.rooms || []);

        const params: CalculLaizeParams = {
            laizeCalcProps,
            rooms,
            openings,
            roomItems,
            walls: originalMapWalls,
        };

        const logName = 'from LaizeCalcDialogProvider';
        if (roomType === RoomType.Stairs) {
            const laizeStairsResult = LaizeStairs.calculLaizeStairs({
                ...params,
                rooms: rooms.map((r) => ({
                    ...r,
                    staircaseIndex:
                        mapInputs.rooms
                            ?.filter((x) => x.type === roomType)
                            .map((x) => x.roomId)
                            .indexOf(r.roomId) ?? -1,
                })),
                // iteration: state.iteration,
                logName,
            });
            setLaizeStairsResult(laizeStairsResult);
        } else {
            const laizeResult = LaizeV3.calculLaizeV3({
                ...params,
                iteration: state.iteration,
                logName,
                requestedBy: 'LaizeCalcDialogProvider',
            });

            const iteration = toCurrentIteration(state.iteration!, laizeResult);
            if (state.iteration !== iteration) {
                update({ iteration });
            }

            setLaizeResult({
                result: laizeResult,
                props: laizeCalcProps,
                validCurrentWallStarts: checkIfCurrentWallStartsAreValid(laizeResult, state.rooms!),
            });
        }
    }, [state]);

    return (
        <LaizeCalcDialogContext.Provider
            value={{
                laizeResult: laizeResult?.result,
                laizeStairsResult,
                validCurrentWallStarts: laizeResult?.validCurrentWallStarts,
                state,
                update,
                focusedBands,
                setFocusedBands,
                clearFocusedBands,
                zoomedBands,
                setZoomedBands,
                zoomedStair,
                setZoomedStair,
            }}>
            {children}
        </LaizeCalcDialogContext.Provider>
    );
};

function toLaizeCalcDialogState(map: Partial<MapContextState>): LaizeCalcDialogState {
    const { rooms, openings, roomItems, laizePropsByType = [], type: roomType } = getMapByType(map);
    const laizePropsToUse = laizePropsByType[0];

    let filteredRooms = rooms;
    let filteredOpenings = openings;
    let filteredRoomItems = roomItems;

    if (laizePropsToUse && laizePropsToUse.roomIds) {
        filteredRooms = rooms.filter((r) => laizePropsToUse.roomIds!.includes(r.roomId!));
        const filteredRoomIds = filteredRooms.map((r) => r.roomId!);
        filteredOpenings = openings!.filter((o) => filteredRoomIds.includes(o.roomId!));
        filteredRoomItems = roomItems!.filter(
            (ri) => ri.roomId === undefined || filteredRoomIds.includes(ri.roomId)
        );
    }

    const LAIZE_MARGIN = laizePropsToUse?.type === ProductType.Rouleau ? 10 : 0;
    const LAIZE_CONSECUTIVE_BANDS_MARGIN = laizePropsToUse?.type === ProductType.Rouleau ? 10 : 0;
    return {
        mapInputs: { rooms: map.rooms, laizeProps: map.laizeProps },
        rooms: filteredRooms,
        roomType,
        openings: filteredOpenings,
        roomItems: filteredRoomItems,

        iteration: 0,
        rotationAngle: 0,

        laizeProps: laizePropsToUse,
        bandWidth: laizePropsToUse?.largeurCm,
        laizeMargin: LAIZE_MARGIN,
        raccordLength: laizePropsToUse?.raccordCm,
        motifWidth: laizePropsToUse?.largeurMarginCm,
        motifLength: laizePropsToUse?.longueurMarginCm,
        longueurCm: laizePropsToUse?.longueurCm,
        laizeConsecutiveBandsMargin: LAIZE_CONSECUTIVE_BANDS_MARGIN,
        produit: laizePropsToUse?.produit,
        productType: laizePropsToUse?.type || ProductType.Rouleau,
    };
}

function toCurrentIteration(stateIteration: number, laizeResult?: LaizeCalculatorResult) {
    if (laizeResult && stateIteration > laizeResult.roomsBandsDetailsContiguous.length) {
        const iteration = laizeResult.roomsBandsDetailsContiguous.length;
        return iteration;
    }
    return stateIteration;
}

function checkIfCurrentWallStartsAreValid(
    laizeResult: LaizeCalculatorResult | undefined,
    rooms: IRoom[]
): Array<{ roomId: string; isValid: boolean }> {
    if (!laizeResult) {
        return [];
    }
    return rooms.map((room) => {
        const possibleFlooringWallStart =
            laizeResult.possibleFlooringWallStartByRoomId?.find((item) => item.roomId === room.roomId!)!.value || [];
        const found = possibleFlooringWallStart.find((p) => p.wallId === room.flooringWallStart?.wallId);
        const isValid = found !== undefined && (found.laizeResult ?? 0) > 0;
        return { roomId: room.roomId!, isValid };
    });
}

export function updateRoomsWallStartDirection(
    mapRooms: IRoom[] = [],
    laizeDialogRooms: IRoom[] = [],
    validCurrentWallStarts: Array<{ roomId: string; isValid: boolean }> = []
): IRoom[] {
    return mapRooms.map((mapRoom) => {
        const room = laizeDialogRooms.find((r) => r.roomId === mapRoom.roomId)!;
        if (room) {
            const flooringWallStart = room.flooringWallStart;
            if (Boolean(flooringWallStart) && mapRoom.flooringWallStart?.sideName !== flooringWallStart?.sideName) {
                mapRoom.flooringWallStart = flooringWallStart;
            }
            if (
                validCurrentWallStarts.find((x) => x.roomId === room.roomId && !x.isValid) &&
                mapRoom.lameFlooringLayout === LameFlooringLayout.LamePleine
            ) {
                mapRoom.lameFlooringLayout = LameFlooringLayout.ClassiqueVertical;
                mapRoom.flooringWallStartValidated = false;
            }
        }
        mapRoom.flooringWallStartValidated = true;
        return mapRoom;
    });
}
